'use client';

import { useVirtualizer } from '@tanstack/react-virtual';
import { type MouseEvent, memo, type Ref, useCallback, useMemo, useState } from 'react';

import { ListItemRow } from '~/components/list/ListItemRow';
import { ListViewHeader, type ListViewSort } from '~/components/list/ListViewHeader';
import { getListViewMinWidth, type ListViewColumnField } from '~/constants/listViewColumns';
import { type ListViewItem } from '~/utils/formatItemsListResponse';

const LIST_ROW_HEIGHT = 44;

export type ListViewProps = {
  emptyStateMessage: string;
  hasNextPage: boolean;
  isFetchingNextPage: boolean;
  isLoading: boolean;
  items: ListViewItem[];
  onItemClick?: (item: ListViewItem, event: MouseEvent<HTMLButtonElement>) => void;
  onItemDoubleClick?: (item: ListViewItem, event: MouseEvent<HTMLButtonElement>) => void;
  onSortChange: (sort: ListViewSort) => void;
  selectedBoardIds: ReadonlySet<string>;
  selectedClipIds: ReadonlySet<string>;
  sentinelRef?: Ref<HTMLDivElement>;
  sort: ListViewSort;
  visibleColumns: ListViewColumnField[];
};

export const ListView = memo(
  ({
    emptyStateMessage,
    hasNextPage,
    isFetchingNextPage,
    isLoading,
    items,
    onItemClick,
    onItemDoubleClick,
    onSortChange,
    selectedBoardIds,
    selectedClipIds,
    sentinelRef,
    sort,
    visibleColumns,
  }: ListViewProps) => {
    const [scrollElement, setScrollElement] = useState<HTMLElement | null>(null);

    const rowVirtualizer = useVirtualizer({
      count: items.length,
      estimateSize: () => LIST_ROW_HEIGHT,
      getScrollElement: () => scrollElement,
      overscan: 8,
    });

    const virtualRows = rowVirtualizer.getVirtualItems();

    const getIsSelected = useCallback(
      (item: ListViewItem) => {
        if (item.type === 'board') {
          return selectedBoardIds.has(item.id);
        }

        return selectedClipIds.has(item.id);
      },
      [selectedBoardIds, selectedClipIds],
    );

    const showEmptyState = !isLoading && items.length === 0;
    const minWidth = useMemo(() => getListViewMinWidth(visibleColumns), [visibleColumns]);

    const content = useMemo(() => {
      if (isLoading) {
        return (
          <div className="space-y-0">
            {Array.from({ length: 6 }).map((_, index) => (
              <div className="h-11 animate-pulse border-neutral-4 border-b bg-neutral-3" key={index} />
            ))}
          </div>
        );
      }

      if (showEmptyState) {
        return <p className="px-4 py-8 text-center text-12 text-neutral-10">{emptyStateMessage}</p>;
      }

      return (
        <div className="relative w-full" style={{ height: rowVirtualizer.getTotalSize() }}>
          {virtualRows.map((virtualRow) => {
            const item = items[virtualRow.index];

            if (!item) {
              return null;
            }

            return (
              <div
                className="absolute top-0 left-0 w-full min-w-0 overflow-hidden"
                key={item.id}
                style={{ transform: `translateY(${virtualRow.start}px)` }}
              >
                <ListItemRow
                  isSelected={getIsSelected(item)}
                  item={item}
                  onClick={onItemClick}
                  onDoubleClick={onItemDoubleClick}
                  visibleColumns={visibleColumns}
                />
              </div>
            );
          })}
        </div>
      );
    }, [
      getIsSelected,
      isLoading,
      items,
      onItemClick,
      onItemDoubleClick,
      rowVirtualizer,
      showEmptyState,
      emptyStateMessage,
      virtualRows,
      visibleColumns,
    ]);

    return (
      <section
        aria-busy={isLoading}
        aria-label="List"
        className="relative flex min-h-0 flex-1 flex-col overflow-hidden"
      >
        <div
          className="min-w-0 flex-1 overflow-x-auto overflow-y-auto"
          data-testid="list-view-scroll"
          ref={setScrollElement}
        >
          <div data-testid="list-view-table" style={{ width: `max(100%, ${minWidth}px)` }}>
            <ListViewHeader onSortChange={onSortChange} sort={sort} visibleColumns={visibleColumns} />
            {content}
          </div>
          {hasNextPage && <div aria-hidden="true" className="h-1" ref={sentinelRef} />}
          {isFetchingNextPage && <p className="px-4 py-3 text-center text-11 text-neutral-10">Loading more...</p>}
        </div>
      </section>
    );
  },
);

ListView.displayName = 'ListView';
